import { IcoFileAnalysis, IcoFolder } from "assets/icons";
import clsx from "clsx";
import colors from "colors";
import { BackButton, Button, Loading } from "components";
import ApplicationSummary from "components/ApplicationSummary";
import { FolderDisplayItem } from "components/FolderDisplay";
import { ErrorHelper } from "helpers";
import { useIsMobile } from "hooks/useIsMobile";
import { Application } from "models/Application";
import { ApplicationStatus } from "models/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ApplicationDocumentService } from "services/applicationDocument";
import { ApplicationService } from "services/applicationService";
import CancelApplicationModal from "./modules/CancelApplicationModal";
import CancelApprovedApplicationModal from "./modules/CancelApprovedApplicationModal";
import "./styles.scss";

type ApplicationDetailParams = {
	applicationId: string;
};

export default function ApplicationDetail() {
	const navigate = useNavigate();
	const isMobile = useIsMobile();
	const [application, setApplication] = useState<Application>();
	const [loading, setLoading] = useState(true);
	const [cancelModalOpen, setCancelModalOpen] = useState(false);
	const applicationService = useMemo(ApplicationService.getInstance, []);
	const applicationDocumentService = useMemo(ApplicationDocumentService.getInstance, []);
	const { applicationId } = useParams<keyof ApplicationDetailParams>() as ApplicationDetailParams;

	const navigateToMessages = useCallback(
		() =>
			navigate("./analises", {
				state: { prevRoute: `/solicitacao/${applicationId}` }
			}),
		[navigate, applicationId]
	);

	const fetchApplication = async () => {
		setLoading(true);
		try {
			const response = await applicationService.retrieveMyApplication(applicationId);
			setApplication(response);
		} catch (error) {
			toast.error(ErrorHelper.getResponseErrorMessage(error));
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchApplication();
	}, [applicationId]);

	const openCancelModal = () => setCancelModalOpen(true);
	const [cancelApprovedOpen, setCancelApprovedOpen] = useState(false);
	const openCancelApprovedModal = () => setCancelApprovedOpen(true);

	return (
		<div className="application-detail-page">
			<div className="application-detail-header">
				<div className="application-back-title">
					{!isMobile && <BackButton prevRoute="/" />}
					<div className="application-title">
						Processo
						{!!application && ` - ${application?.applicationProcessCode}`}
					</div>
				</div>
				{!isMobile && (
					<div className="flex gap-6">
						{!application?.isInFinalStatus() &&
							application?.status !== ApplicationStatus.APROVADO &&
							application?.status !== ApplicationStatus.PENDENTE_CANCELAMENTO && (
								<Button
									kind="default"
									hierarchy="secondary"
									size="small"
									className="bg-transparent hover:bg-neutral-high-200 text-feedback-negative-pure-500 border-feedback-negative-pure-500"
									disabled={loading}
									onClick={openCancelModal}
								>
									Cancelar
								</Button>
							)}
						{application?.status === ApplicationStatus.APROVADO && (
							<Button
								kind="default"
								hierarchy="secondary"
								size="small"
								className="bg-transparent hover:bg-neutral-high-200 text-feedback-negative-pure-500 border-feedback-negative-pure-500"
								disabled={loading}
								onClick={openCancelApprovedModal}
							>
								Cancelar
							</Button>
						)}
						<Button kind="default" hierarchy="primary" size="small" onClick={navigateToMessages} disabled={loading}>
							<IcoFileAnalysis size="16" color={colors.neutral["high-pure-50"]} />
							Exibir análises
						</Button>
					</div>
				)}
			</div>
			{loading || !application ? (
				<Loading />
			) : (
				<div
					className={clsx("application-detail-content", {
						"pb-36": isMobile && !application?.isInFinalStatus(),
						"pb-20": isMobile && application?.isInFinalStatus()
					})}
				>
					<ApplicationSummary values={application} />
					<div className="flex flex-col gap-4 pt-10">
						<div className="font-semibold pb-2 border-b-2 border-neutral-high-200">Documentos</div>
						<div className="document-folder-section-container">
							{application?.documentFolders.map((folder) => (
								<FolderDisplayItem
									key={folder.id}
									folder={folder}
									applicationDocumentService={applicationDocumentService}
									defaultOpen={[""]}
									columns={[
										{
											name: (
												<div className="flex flex-column gap-2 items-center font-body-regular-small">
													<IcoFolder />
													{folder.documentTypeName}
												</div>
											),
											size: 12
										}
									]}
								/>
							))}
						</div>
					</div>
				</div>
			)}
			{isMobile && (
				<div className="button-footer">
					<Button kind="default" hierarchy="primary" size="small" onClick={navigateToMessages}>
						Exibir análises
					</Button>
					{!application?.isInFinalStatus() &&
						application?.status !== ApplicationStatus.APROVADO &&
						application?.status !== ApplicationStatus.PENDENTE_CANCELAMENTO && (
							<Button hierarchy="secondary" size="small" disabled={loading} onClick={openCancelModal}>
								Cancelar solicitação
							</Button>
						)}
					{application?.status === ApplicationStatus.APROVADO && (
						<Button hierarchy="secondary" size="small" disabled={loading} onClick={openCancelApprovedModal}>
							Cancelar isenção
						</Button>
					)}
				</div>
			)}
			<CancelApplicationModal applicationId={applicationId} isOpen={cancelModalOpen} onClose={() => setCancelModalOpen(false)} />
			<CancelApprovedApplicationModal
				applicationId={applicationId}
				isOpen={cancelApprovedOpen}
				onClose={() => setCancelApprovedOpen(false)}
				onSuccess={fetchApplication}
			/>
		</div>
	);
}
